// src/pages/student/StudentEnrollments.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchStudentEnrollments } from '../../api/studentPortal';
import { usePortalStudentId, useIsParentViewingChild } from '../../hooks/usePortalStudentId';
import ParentPortalHeader from '../../components/parent/ParentPortalHeader';
import ListPageLayout, {
    ListPageHeader,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../components/layout/ListPageLayout';
import PaginationFooter from '../../components/common/PaginationFooter';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import useSortedClientPagination from '../../hooks/useSortedClientPagination';
import { studentEnrollmentSortColumns } from '../../config/tableSortColumns';
import { formatDate } from '../../utils/date';
import { localizeBatchName } from '../../utils/displayNames';

function useDebounced(v, d = 200) {
    const [x, setX] = useState(v);
    useEffect(() => { const t = setTimeout(() => setX(v), d); return () => clearTimeout(t); }, [v, d]);
    return x;
}

const StudentEnrollments = () => {
    const { t } = useTranslation();
    const childStudentId = usePortalStudentId();
    const isParentView = useIsParentViewingChild();
    const walletBase = isParentView ? '/parent/wallet' : '/student/wallet';
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);

    useEffect(() => {
        let aborted = false;
        (async () => {
            setLoading(true);
            setErr('');
            try {
                const data = await fetchStudentEnrollments(childStudentId);
                if (!aborted) setRows(Array.isArray(data) ? data : []);
            } catch (e) {
                if (!aborted) { setRows([]); setErr(t('pages.studentEnrollments.loadFailed')); }
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => { aborted = true; };
    }, [childStudentId, t]);

    const filtered = useMemo(() => {
        const term = (dq || '').trim().toLowerCase();
        if (!term) return rows || [];
        const pick = (x) => (x ?? '').toString().toLowerCase();
        return (rows || []).filter((r) =>
            [r.levelName, r.batchName, r.enrolledDate]
                .some((v) => pick(v).includes(term))
        );
    }, [rows, dq]);

    const pagination = useSortedClientPagination(filtered, {
        columns: studentEnrollmentSortColumns,
        resetKey: dq,
    });

    const shown = filtered.length, total = rows.length;

    if (loading) return <ListPageLayout><ListPageHeader title={t('pages.studentEnrollments.title')} subtitle={t('common.loading')} showBack={false} /><ListLoadingSpinner /></ListPageLayout>;
    if (err) {
        return (
            <ListPageLayout>
                <ListPageHeader title={t('pages.studentEnrollments.title')} subtitle={t('pages.studentEnrollments.subtitle')} />
                <div className="banner-danger">{err}</div>
            </ListPageLayout>
        );
    }

    return (
        <ListPageLayout>
            <ParentPortalHeader />
            <ListPageHeader
                title={t('pages.studentEnrollments.title')}
                subtitle={t('pages.studentEnrollments.subtitle')}
            />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.studentEnrollments.searchPlaceholder')}
                shown={shown}
                total={total}
                entityLabel={t('pages.studentEnrollments.entityLabel')}
            />

            {filtered.length === 0 ? (
                <ListEmptyState
                    message={q ? t('pages.studentEnrollments.noMatch') : t('pages.studentEnrollments.empty')}
                />
            ) : (
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t('common.idColumn')} columnKey="id" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('table.level')} columnKey="level" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('table.batch')} columnKey="batch" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('table.enrolledDate')} columnKey="enrolledDate" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <th className="table-header-cell text-right">{t('table.payment')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pagination.pageItems.map((r, i) => (
                                    <tr key={r.enrollmentId || i} className="table-row">
                                        <td className="table-cell text-gray-600">#{r.enrollmentId ?? '—'}</td>
                                        <td className="table-cell font-medium text-gray-900">{r.levelName || '—'}</td>
                                        <td className="table-cell text-gray-700">{localizeBatchName(r.batchName, t) || '—'}</td>
                                        <td className="table-cell text-gray-600">{r.enrolledDate ? formatDate(r.enrolledDate) : '—'}</td>
                                        <td className="table-cell text-right">
                                            {r.enrollmentId ? (
                                                <Link
                                                    to={`${walletBase}?purpose=ENROLLMENT_FEE&enrollmentId=${r.enrollmentId}`}
                                                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                                                >
                                                    {t('pages.studentEnrollments.payOnline')}
                                                </Link>
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.total > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </ListTableCard>
            )}
        </ListPageLayout>
    );
};

export default StudentEnrollments;
